import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { riderAPI, rideAPI } from '../../lib/api';
import { formatCurrency, getStatusColor, getStatusText, getTierColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { MapPin, Gift, Calendar, CreditCard, Loader2 } from 'lucide-react';

const RiderDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [recentRides, setRecentRides] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, loyaltyRes, ridesRes] = await Promise.all([
        riderAPI.getProfile().catch(() => null),
        riderAPI.getLoyalty().catch(() => null),
        rideAPI.getHistory().catch(() => null),
      ]);
      
      if (profileRes) setProfile(profileRes.data);
      if (loyaltyRes) setLoyalty(loyaltyRes.data);
      if (ridesRes) setRecentRides(ridesRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const loyaltyProgress = loyalty?.next_tier 
    ? ((loyalty.total_rides - (loyalty.tiers[loyalty.current_tier]?.min_rides || 0)) / 
       (loyalty.tiers[loyalty.next_tier]?.min_rides - (loyalty.tiers[loyalty.current_tier]?.min_rides || 0))) * 100
    : 100;

  return (
    <div className="p-6" data-testid="rider-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight">
            Welcome back{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
          </h1>
          <Link to="/rider/book">
            <Button className="rounded-full bg-red-600 hover:bg-red-700 font-bold uppercase tracking-wider px-8 animate-pulse-red">
              <MapPin className="w-4 h-4 mr-2" />
              Book a Ride
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/rider/book">
                <Card className="bg-gradient-to-br from-red-500 to-red-600 border-0 hover:shadow-lg hover:shadow-red-600/20 transition-all cursor-pointer">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      <MapPin className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-white uppercase">Ride Now</h3>
                      <p className="text-white/80 text-sm">Get picked up in minutes</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/rider/schedule">
                <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:shadow-md transition-all cursor-pointer">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                      <Calendar className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-slate-900 dark:text-white uppercase">Schedule</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">Plan up to 14 days ahead</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Recent Rides */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display uppercase tracking-tight">Recent Rides</CardTitle>
                <Link to="/rider/history" className="text-red-600 hover:text-red-500 text-sm font-medium">
                  View all
                </Link>
              </CardHeader>
              <CardContent>
                {recentRides.length > 0 ? (
                  <div className="space-y-4">
                    {recentRides.map((ride) => (
                      <div 
                        key={ride.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {ride.distance_miles?.toFixed(1)} miles
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {new Date(ride.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {formatCurrency(ride.actual_fare || ride.estimated_fare)}
                          </p>
                          <Badge className={`${getStatusColor(ride.status)} text-xs`}>
                            {getStatusText(ride.status)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                    No rides yet. Book your first ride!
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Loyalty Card */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                  <Gift className="w-5 h-5 text-red-600" />
                  Loyalty Rewards
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <span className={`text-3xl font-display font-bold capitalize ${getTierColor(loyalty?.current_tier || 'bronze')}`}>
                    {loyalty?.current_tier || 'Bronze'}
                  </span>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {loyalty?.discount_percent || 0}% discount on rides
                  </p>
                </div>
                
                {loyalty?.next_tier && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Progress to {loyalty.next_tier}</span>
                      <span className="font-medium text-slate-900 dark:text-white">
                        {loyalty.rides_to_next_tier} rides left
                      </span>
                    </div>
                    <Progress value={loyaltyProgress} className="h-2" />
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Total Rides</span>
                    <span className="font-semibold text-slate-900 dark:text-white">{loyalty?.total_rides || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-slate-500 dark:text-slate-400">Points</span>
                    <span className="font-semibold text-slate-900 dark:text-white">{loyalty?.loyalty_points || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-red-600" />
                  Payments
                </CardTitle>
                <Link to="/rider/payments" className="text-red-600 hover:text-red-500 text-sm font-medium">
                  Manage
                </Link>
              </CardHeader>
              <CardContent>
                {profile?.saved_payment_methods?.length > 0 ? (
                  <div className="space-y-3">
                    {profile.saved_payment_methods.slice(0, 2).map((method) => (
                      <div 
                        key={method.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                      >
                        <CreditCard className="w-5 h-5 text-slate-400" />
                        <span className="font-medium text-slate-900 dark:text-white">
                          •••• {method.last_four}
                        </span>
                        {method.is_default && (
                          <Badge variant="outline" className="ml-auto text-xs">Default</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Link to="/rider/payments">
                    <Button variant="outline" className="w-full">
                      Add Payment Method
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiderDashboard;
